/**
 * DomainHistoryTab Component
 *
 * Displays historical WHOIS data for domains showing changes over time.
 * Each history entry is rendered as formatted JSON in a markdown code block.
 *
 * @component
 */

import { NoInformation as NoInformationIcon } from '@praetorian-chariot/ui';

import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { NoData } from '@/components/ui/NoData';
import { Seed } from '@/types';

interface DomainHistoryTabProps {
  /** The seed to display history for */
  seed: Seed;
  /** Array of historical WHOIS records */
  history?: string[];
}

/**
 * DomainHistoryTab displays historical WHOIS data changes
 *
 * Shows a timeline of domain registration data changes retrieved from
 * WHOIS history lookups. Each entry is displayed as formatted JSON.
 *
 * @param _seed - The seed object (reserved for future use)
 * @param history - Array of WHOIS history records as JSON strings
 */
export function DomainHistoryTab({
  seed: _seed,
  history,
}: DomainHistoryTabProps) {
  if (!history || history.length === 0) {
    return (
      <NoData
        icon={
          <NoInformationIcon className="text-chariot-stroke mb-4 text-[100px]" />
        }
        title="No History Found"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 [&_code]:p-0!">
      {history.map((data: string, i: number) => (
        <MarkdownPreview
          key={i}
          source={`\`\`\`json\n${JSON.stringify(data, null, 2)}\n\`\`\``}
        />
      ))}
    </div>
  );
}
